import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

/**
 * Reliable cart review rotation.
 * Liquid always renders a fallback review first. This script enhances it by
 * selecting one of the curated reviews, replacing the fallback, and persisting
 * attribution after every Shopify cart drawer refresh.
 */
(() => {
  const SELECTOR = 'cart-review-rotator:not([data-initialized="true"])';

  const getSelectedIndex = (element, optionCount) => {
    const forcedRaw = new URLSearchParams(window.location.search).get('cart_review_variant');
    const forcedVariant = forcedRaw === null ? null : Number(forcedRaw);

    if (Number.isInteger(forcedVariant) && forcedVariant >= 1 && forcedVariant <= optionCount) {
      return forcedVariant - 1;
    }

    const storageKey = element.dataset.storageKey;

    try {
      const storedRaw = window.localStorage.getItem(storageKey);

      if (storedRaw !== null) {
        const storedIndex = Number(storedRaw);

        if (Number.isInteger(storedIndex) && storedIndex >= 0 && storedIndex < optionCount) {
          return storedIndex;
        }
      }

      const randomIndex = Math.floor(Math.random() * optionCount);
      window.localStorage.setItem(storageKey, String(randomIndex));
      return randomIndex;
    } catch (error) {
      return Math.floor(Math.random() * optionCount);
    }
  };

  const persistAttribution = (element, selectedOption, selectedIndex) => {
    const attribution = {
      __cart_review_test: element.dataset.testId || 'cart-review-v1',
      __cart_review_product: element.dataset.productHandle || '',
      __cart_review_id: selectedOption.dataset.reviewId || '',
      __cart_review_variant: String(selectedIndex + 1),
      __cart_review_title: selectedOption.dataset.reviewTitle || '',
      __cart_review_author: selectedOption.dataset.reviewAuthor || '',
    };

    document.querySelectorAll('form[action*="/cart"]').forEach((form) => {
      Object.entries(attribution).forEach(([key, value]) => {
        let input = form.querySelector(`input[name="attributes[${key}]"]`);

        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = `attributes[${key}]`;
          form.appendChild(input);
        }

        input.value = value;
      });
    });

    const signature = JSON.stringify(attribution);

    if (window.__brokenheartCartReviewAttribution !== signature) {
      window.__brokenheartCartReviewAttribution = signature;

      const root = window.Shopify?.routes?.root || '/';
      fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ attributes: attribution }),
      }).catch(() => null);
    }

    if (window.Shopify?.analytics?.publish) {
      const publishResult = window.Shopify.analytics.publish('brokenheart:cart_review_impression', {
        test_id: attribution.__cart_review_test,
        product_handle: attribution.__cart_review_product,
        review_id: attribution.__cart_review_id,
        review_variant: attribution.__cart_review_variant,
        review_title: attribution.__cart_review_title,
      });

      if (publishResult && typeof publishResult.catch === 'function') {
        publishResult.catch(() => null);
      }
    }
  };

  const initialize = (element) => {
    if (!(element instanceof HTMLElement) || element.dataset.initialized === 'true') return;

    const options = Array.from(element.querySelectorAll('template[data-review-option]'));
    if (!options.length) return;

    const selectedIndex = getSelectedIndex(element, options.length);
    const selectedOption = options[selectedIndex];

    if (!(selectedOption instanceof HTMLTemplateElement)) return;

    element.append(selectedOption.content.cloneNode(true));
    element.dataset.initialized = 'true';
    element.dataset.selectedReviewId = selectedOption.dataset.reviewId || '';

    persistAttribution(element, selectedOption, selectedIndex);
  };

  const scan = (root = document) => {
    if (root instanceof Element && root.matches(SELECTOR)) initialize(root);
    root.querySelectorAll?.(SELECTOR).forEach(initialize);
  };

  const start = () => {
    scan(document);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) scan(node);
        });
      });
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
